import { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  X, Check, Palette, Highlighter, Plus, Minus, Move,
} from 'lucide-react';

// ─── Color palette ───────────────────────────────────────────────────────────
const COLOR_PALETTE = [
  '#000000','#434343','#666666','#999999','#b7b7b7','#cccccc','#d9d9d9','#efefef','#f3f3f3','#ffffff',
  '#980000','#ff0000','#ff9900','#ffff00','#00ff00','#00ffff','#4a86e8','#0000ff','#9900ff','#ff00ff',
  '#e6b8af','#f4cccc','#fce5cd','#fff2cc','#d9ead3','#d0e0e3','#c9daf8','#cfe2f3','#d9d2e9','#ead1dc',
  '#dd7e6b','#ea9999','#f9cb9c','#ffe599','#b6d7a8','#a2c4c9','#a4c2f4','#9fc5e8','#b4a7d6','#d5a6bd',
  '#cc4125','#e06666','#f6b26b','#ffd966','#93c47d','#76a5af','#6d9eeb','#6fa8dc','#8e7cc3','#c27ba0',
  '#a61c00','#cc0000','#e69138','#f1c232','#6aa84f','#45818e','#3c78d8','#3d85c6','#674ea7','#a64d79',
  '#85200c','#990000','#b45f06','#bf9000','#38761d','#134f5c','#1155cc','#0b5394','#351c75','#741b47',
  '#5b0f00','#660000','#783f04','#7f6000','#274e13','#0c343d','#1c4587','#073763','#20124d','#4c1130',
];

// ─── Styled components ────────────────────────────────────────────────────────
const EditorContainer = styled.div`
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background.primary};
  position: relative;
  display: flex;
  flex-direction: column;
  height: 680px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  padding: 8px 12px;
  background-color: ${p => p.theme.colors.background.secondary};
  border-bottom: 1px solid ${p => p.theme.colors.border.light};
  border-radius: ${p => p.theme.borderRadius.md} ${p => p.theme.borderRadius.md} 0 0;
  flex-shrink: 0;
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 22px;
  background-color: ${p => p.theme.colors.border.medium};
  margin: 0 4px;
  flex-shrink: 0;
`;

const TBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${p => p.$active ? p.theme.colors.primary : 'transparent'};
  background-color: ${p => p.$active ? p.theme.colors.primaryLight : 'transparent'};
  color: ${p => p.$active ? p.theme.colors.primary : p.theme.colors.text.secondary};
  border-radius: ${p => p.theme.borderRadius.base};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;

  &:hover {
    background-color: ${p => p.theme.colors.primaryLight};
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const HeadingSelect = styled.select`
  height: 30px;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.base};
  background: ${p => p.theme.colors.background.primary};
  color: ${p => p.theme.colors.text.primary};
  font-family: inherit;
  font-size: 13px;
  padding: 0 24px 0 8px;
  cursor: pointer;
  max-width: 120px;
  -webkit-appearance: none;
  appearance: none;
  color-scheme: normal;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2364748B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 7px center;

  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const FontSizeControl = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.base};
  overflow: hidden;
`;

const FontSizeBtn = styled.button`
  width: 22px;
  height: 100%;
  border: none;
  background: ${p => p.theme.colors.background.secondary};
  color: ${p => p.theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover { background: ${p => p.theme.colors.primaryLight}; color: ${p => p.theme.colors.primary}; }
`;

const FontSizeInput = styled.input`
  width: 34px;
  height: 100%;
  box-sizing: border-box;
  border: none;
  border-left: 1px solid ${p => p.theme.colors.border.light};
  border-right: 1px solid ${p => p.theme.colors.border.light};
  background: ${p => p.theme.colors.background.primary};
  color: ${p => p.theme.colors.text.primary};
  -webkit-text-fill-color: ${p => p.theme.colors.text.primary};
  text-align: center;
  font-size: 13px;
  font-family: inherit;
  line-height: 30px;
  padding: 0;
  -webkit-appearance: none;
  appearance: none;
  color-scheme: normal;

  &:focus { outline: none; }
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
`;

const ColorPickerWrapper = styled.div`
  position: relative;
`;

const ColorDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background: white;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 10px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  min-width: 212px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
`;

const Swatch = styled.button`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: ${p => p.$sel ? '2px solid #333' : '1px solid rgba(0,0,0,0.12)'};
  background-color: ${p => p.$color};
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s;

  &:hover { transform: scale(1.2); position: relative; z-index: 1; }
`;

const RemoveColorRow = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${p => p.theme.colors.text.secondary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 0 6px 0;
  margin-bottom: 4px;
  border-bottom: 1px solid ${p => p.theme.colors.border.light};
  width: 100%;

  &:hover { color: ${p => p.theme.colors.text.primary}; }
`;

const InsertBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  background: ${p => p.theme.colors.background.secondary};
  border-bottom: 1px solid ${p => p.theme.colors.border.light};

  input {
    flex: 1;
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${p => p.theme.colors.border.medium};
    border-radius: ${p => p.theme.borderRadius.base};
    font-size: 13px;
    font-family: inherit;
    background: ${p => p.theme.colors.background.primary};
    color: ${p => p.theme.colors.text.primary};

    &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
    &::placeholder { color: ${p => p.theme.colors.text.tertiary}; }
  }
`;

const InsertRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VideoOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 2px 0 2px 2px;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${p => p.theme.colors.text.secondary};
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] { display: none; }
`;

const ToggleTrack = styled.div`
  width: 32px;
  height: 18px;
  border-radius: 9px;
  background: ${p => p.$on ? p.theme.colors.primary : p.theme.colors.border.medium};
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
`;

const ToggleThumb = styled.div`
  position: absolute;
  top: 2px;
  left: ${p => p.$on ? '16px' : '2px'};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${p => p.theme.borderRadius.base};
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid ${p => p.$primary ? 'transparent' : p.theme.colors.border.medium};
  background: ${p => p.$primary ? p.theme.colors.primary : 'transparent'};
  color: ${p => p.$primary ? 'white' : p.theme.colors.text.secondary};

  &:hover { opacity: 0.85; }
`;

const EditorArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.75rem 2rem;
  font-size: 16px;
  line-height: 1.75;
  color: ${p => p.theme.colors.text.primary};
  font-family: inherit;
  outline: none;
  position: relative;

  &:empty::before {
    content: attr(data-placeholder);
    color: ${p => p.theme.colors.text.tertiary};
    pointer-events: none;
    position: absolute;
    top: 1.75rem;
    left: 2rem;
  }

  h1 { font-size: 2rem; font-weight: 700; margin: 1.5rem 0 0.6rem; line-height: 1.2; }
  h2 { font-size: 1.5rem; font-weight: 600; margin: 1.25rem 0 0.5rem; line-height: 1.3; }
  h3 { font-size: 1.2rem; font-weight: 600; margin: 1rem 0 0.4rem; line-height: 1.35; }
  h4 { font-size: 1rem; font-weight: 600; margin: 0.75rem 0 0.35rem; }

  p { margin-bottom: 0.6rem; }

  ul, ol { padding-left: 1.75rem; margin-bottom: 0.75rem; }
  li { margin-bottom: 0.2rem; }

  a { color: ${p => p.theme.colors.primary}; text-decoration: underline; }

  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  s { text-decoration: line-through; }

  blockquote {
    border-left: 3px solid ${p => p.theme.colors.primary};
    padding-left: 1rem;
    margin: 1rem 0;
    color: ${p => p.theme.colors.text.secondary};
    font-style: italic;
  }

  pre {
    background: ${p => p.theme.colors.background.secondary};
    border: 1px solid ${p => p.theme.colors.border.light};
    border-radius: ${p => p.theme.borderRadius.base};
    padding: 1rem;
    font-family: monospace;
    font-size: 14px;
    overflow-x: auto;
    margin: 1rem 0;
  }

  img {
    display: block;
    max-width: 100%;
    border-radius: 8px;
    margin: 1.5rem auto;
    cursor: pointer;
  }

  img.img-selected,
  video.img-selected {
    outline: 2px solid ${p => p.theme.colors.primary};
    outline-offset: 2px;
  }

  iframe, video {
    display: block;
    max-width: 100%;
    border-radius: 8px;
    margin: 1.5rem auto;
    border: none;
    cursor: pointer;
  }

  hr {
    border: none;
    border-top: 1px solid ${p => p.theme.colors.border.medium};
    margin: 2rem 0;
  }

  &:focus { outline: none; }
`;

// Resize overlay
const ResizeBox = styled.div`
  position: absolute;
  border: 2px solid ${p => p.theme.colors.primary};
  box-sizing: border-box;
  pointer-events: none;
  z-index: 20;
`;

const ResizeHandle = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border: 2px solid ${p => p.theme.colors.primary};
  border-radius: 2px;
  pointer-events: all;

  ${p => p.$pos === 'nw' && 'top:-5px;left:-5px;cursor:nwse-resize;'}
  ${p => p.$pos === 'ne' && 'top:-5px;right:-5px;cursor:nesw-resize;'}
  ${p => p.$pos === 'sw' && 'bottom:-5px;left:-5px;cursor:nesw-resize;'}
  ${p => p.$pos === 'se' && 'bottom:-5px;right:-5px;cursor:nwse-resize;'}
  ${p => p.$pos === 'n'  && 'top:-5px;left:calc(50% - 5px);cursor:ns-resize;'}
  ${p => p.$pos === 's'  && 'bottom:-5px;left:calc(50% - 5px);cursor:ns-resize;'}
  ${p => p.$pos === 'e'  && 'right:-5px;top:calc(50% - 5px);cursor:ew-resize;'}
  ${p => p.$pos === 'w'  && 'left:-5px;top:calc(50% - 5px);cursor:ew-resize;'}
`;

const DragHandle = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  background: ${p => p.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  pointer-events: all;
  color: white;
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  z-index: 1;

  &:active { cursor: grabbing; }
`;

const MediaControls = styled.div`
  position: absolute;
  bottom: -38px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${p => p.theme.colors.background.primary};
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 3px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  white-space: nowrap;
  pointer-events: all;
  z-index: 2;
`;

const MediaControlBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  height: 24px;
  border: 1px solid ${p => p.$active ? p.theme.colors.primary : p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.base};
  background: ${p => p.$active ? p.theme.colors.primaryLight : 'transparent'};
  color: ${p => p.$active ? p.theme.colors.primary : p.theme.colors.text.secondary};
  font-size: 11px;
  font-weight: ${p => p.$active ? 600 : 400};
  font-family: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const DropLine = styled.div`
  position: absolute;
  left: 2rem;
  right: 2rem;
  height: 2px;
  background: ${p => p.theme.colors.primary};
  pointer-events: none;
  z-index: 15;
  border-radius: 1px;

  &::before, &::after {
    content: '';
    position: absolute;
    top: -3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.theme.colors.primary};
  }
  &::before { left: -4px; }
  &::after  { right: -4px; }
`;

// ─── Component ────────────────────────────────────────────────────────────────
export const HtmlContentUploader = ({ value = '', onChange, placeholder = 'Start writing your project story...' }) => {
  const containerRef = useRef(null);
  const editorRef    = useRef(null);
  const isEditingRef = useRef(false);
  const savedSelRef  = useRef(null);
  const textCPRef    = useRef(null);
  const hlCPRef      = useRef(null);

  const [headingValue, setHeadingValue] = useState('p');
  const [fontSize,     setFontSize]     = useState(16);
  const [textColor,    setTextColor]    = useState('#000000');
  const [hlColor,      setHlColor]      = useState('#ffff00');
  const [showTextCP,   setShowTextCP]   = useState(false);
  const [showHlCP,     setShowHlCP]     = useState(false);
  const [showMediaBar, setShowMediaBar] = useState(false);
  const [showLinkBar,  setShowLinkBar]  = useState(false);
  const [mediaUrl,     setMediaUrl]     = useState('');
  const [linkUrl,      setLinkUrl]      = useState('');
  const [hideControls,  setHideControls]  = useState(false);
  const [loopVideo,     setLoopVideo]     = useState(false);
  const [autoplayVideo, setAutoplayVideo] = useState(false);
  const [selectedImg,  setSelectedImg]  = useState(null);
  const [overlay,      setOverlay]      = useState(null);
  const [dropLine,     setDropLine]     = useState(null);
  const [isFullWidth,  setIsFullWidth]  = useState(false);

  // ── Sync external value → DOM (skip when user is typing) ──────────────────
  useEffect(() => {
    if (editorRef.current && !isEditingRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  // ── Close color pickers when clicking outside ─────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (!textCPRef.current?.contains(e.target)) setShowTextCP(false);
      if (!hlCPRef.current?.contains(e.target))   setShowHlCP(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Deselect image when clicking outside container ────────────────────────
  useEffect(() => {
    if (!selectedImg) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) deselect();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImg]);

  // ── Detect heading/fontSize at cursor ─────────────────────────────────────
  const detectState = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    let node = sel.anchorNode;
    if (node?.nodeType === 3) node = node.parentElement;

    // Walk up to find block tag
    const blockMap = { H1: 'h1', H2: 'h2', H3: 'h3', H4: 'h4', BLOCKQUOTE: 'blockquote', PRE: 'pre' };
    let cur = node;
    let found = 'p';
    while (cur && cur !== editorRef.current) {
      if (blockMap[cur.tagName]) { found = blockMap[cur.tagName]; break; }
      cur = cur.parentElement;
    }
    setHeadingValue(found);

    // Read computed font size
    if (node && editorRef.current?.contains(node)) {
      const fs = window.getComputedStyle(node).fontSize;
      const px = parseFloat(fs);
      if (!isNaN(px)) setFontSize(Math.round(px));
    }
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const notify = useCallback(() => {
    if (!editorRef.current) return;
    isEditingRef.current = true;
    onChange(editorRef.current.innerHTML);
    setTimeout(() => { isEditingRef.current = false; }, 100);
  }, [onChange]);

  const saveSel = useCallback(() => {
    const sel = window.getSelection();
    if (sel?.rangeCount > 0) savedSelRef.current = sel.getRangeAt(0).cloneRange();
  }, []);

  const restoreSel = useCallback(() => {
    const sel = window.getSelection();
    if (sel && savedSelRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedSelRef.current);
    }
  }, []);

  const exec = useCallback((cmd, val = null) => {
    restoreSel();
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    notify();
  }, [restoreSel, notify]);

  // ── Font size ─────────────────────────────────────────────────────────────
  const applyFontSize = useCallback((size) => {
    const s = Math.min(96, Math.max(8, size));
    setFontSize(s);
    restoreSel();
    const sel = window.getSelection();
    if (sel?.rangeCount > 0 && !sel.isCollapsed) {
      // Use fontSize 7 as marker, then replace with span
      document.execCommand('styleWithCSS', false, false);
      document.execCommand('fontSize', false, '7');
      editorRef.current?.querySelectorAll('font[size="7"]').forEach(tag => {
        const span = document.createElement('span');
        span.style.fontSize = s + 'px';
        while (tag.firstChild) span.appendChild(tag.firstChild);
        tag.parentNode.replaceChild(span, tag);
      });
      notify();
    }
    editorRef.current?.focus();
  }, [restoreSel, notify]);

  // ── Strip formatting on paste ─────────────────────────────────────────────
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text) {
      document.execCommand('insertText', false, text);
      notify();
    }
  }, [notify]);

  // ── Block format ──────────────────────────────────────────────────────────
  const applyBlock = useCallback((format) => {
    exec('formatBlock', format);
  }, [exec]);

  // ── Colors ────────────────────────────────────────────────────────────────
  const applyTextColor = useCallback((color) => {
    setTextColor(color);
    exec('foreColor', color);
    setShowTextCP(false);
  }, [exec]);

  const applyHighlight = useCallback((color) => {
    setHlColor(color);
    exec('hiliteColor', color);
    setShowHlCP(false);
  }, [exec]);

  // ── Link ──────────────────────────────────────────────────────────────────
  const doInsertLink = useCallback(() => {
    const url = linkUrl.trim();
    if (!url) return;
    exec('createLink', url.startsWith('http') ? url : 'https://' + url);
    setShowLinkBar(false);
    setLinkUrl('');
  }, [linkUrl, exec]);

  // ── Media ─────────────────────────────────────────────────────────────────
  const doInsertMedia = useCallback(() => {
    const url = mediaUrl.trim();
    if (!url) return;
    restoreSel();

    const ytMatch    = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?\s]+)/);
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    const isVideoFile = /\.(mp4|webm|ogg)(\?|$)/i.test(url);

    let html = '';
    if (ytMatch) {
      html = `<div style="text-align:center;margin:1.5rem 0;"><iframe width="560" height="315" src="https://www.youtube.com/embed/${ytMatch[1]}" frameborder="0" allowfullscreen style="max-width:100%;border-radius:8px;"></iframe></div>`;
    } else if (vimeoMatch) {
      html = `<div style="text-align:center;margin:1.5rem 0;"><iframe width="560" height="315" src="https://player.vimeo.com/video/${vimeoMatch[1]}" frameborder="0" allowfullscreen style="max-width:100%;border-radius:8px;"></iframe></div>`;
    } else if (isVideoFile) {
      const attrs = [
        `src="${url}"`,
        !hideControls ? 'controls' : '',
        loopVideo ? 'loop' : '',
        autoplayVideo ? 'autoplay muted' : 'preload="metadata"',
      ].filter(Boolean).join(' ');
      html = `<div style="text-align:center;margin:1.5rem 0;"><video ${attrs} style="max-width:100%;width:560px;border-radius:8px;"></video></div>`;
    } else {
      html = `<img src="${url}" alt="" style="display:block;margin:1.5rem auto;max-width:100%;width:560px;border-radius:8px;cursor:pointer;" />`;
    }

    document.execCommand('insertHTML', false, html);
    notify();
    setShowMediaBar(false);
    setMediaUrl('');
    setHideControls(false);
    setLoopVideo(false);
    setAutoplayVideo(false);
  }, [mediaUrl, hideControls, loopVideo, autoplayVideo, restoreSel, notify]);

  // ── Image select / resize overlay ─────────────────────────────────────────
  const calcOverlay = useCallback((img) => {
    if (!img || !containerRef.current) return null;
    const cr = containerRef.current.getBoundingClientRect();
    const ir = img.getBoundingClientRect();
    return { left: ir.left - cr.left, top: ir.top - cr.top, width: ir.width, height: ir.height };
  }, []);

  const deselect = useCallback(() => {
    if (selectedImg) selectedImg.classList.remove('img-selected');
    setSelectedImg(null);
    setOverlay(null);
    setIsFullWidth(false);
  }, [selectedImg]);

  const selectImg = useCallback((img) => {
    if (selectedImg && selectedImg !== img) selectedImg.classList.remove('img-selected');
    img.classList.add('img-selected');
    setSelectedImg(img);
    setOverlay(calcOverlay(img));
    setIsFullWidth(img.dataset.fullwidth === 'true');
  }, [selectedImg, calcOverlay]);

  const handleEditorClick = useCallback((e) => {
    if (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO') {
      selectImg(e.target);
    } else {
      deselect();
    }
  }, [selectImg, deselect]);

  const startResize = useCallback((e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImg) return;

    const startX  = e.clientX;
    const startY  = e.clientY;
    const startW  = selectedImg.getBoundingClientRect().width;
    const startH  = selectedImg.getBoundingClientRect().height;
    const aspect  = startW / startH;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      let w = startW, h = startH;

      if (handle === 'se' || handle === 'ne' || handle === 'e') {
        w = Math.max(50, startW + dx);
        h = w / aspect;
      } else if (handle === 'sw' || handle === 'nw' || handle === 'w') {
        w = Math.max(50, startW - dx);
        h = w / aspect;
      } else if (handle === 's') {
        h = Math.max(30, startH + dy);
        w = h * aspect;
      } else if (handle === 'n') {
        h = Math.max(30, startH - dy);
        w = h * aspect;
      }

      selectedImg.style.width  = w + 'px';
      selectedImg.style.height = h + 'px';
      setOverlay(calcOverlay(selectedImg));
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      notify();
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [selectedImg, calcOverlay, notify]);

  // ── Drag to reposition ────────────────────────────────────────────────────
  const getMovable = useCallback((el) => {
    if (el.tagName === 'VIDEO' && el.parentElement && el.parentElement !== editorRef.current) {
      return el.parentElement;
    }
    return el;
  }, []);

  const toggleFullWidth = useCallback(() => {
    if (!selectedImg) return;
    const next = !isFullWidth;
    setIsFullWidth(next);
    const movable = getMovable(selectedImg);
    if (next) {
      selectedImg.dataset.fullwidth = 'true';
      selectedImg.style.width = '100%';
      selectedImg.style.height = 'auto';
      selectedImg.style.maxWidth = '100%';
      movable.style.width = '100%';
    } else {
      delete selectedImg.dataset.fullwidth;
      selectedImg.style.width = '560px';
      selectedImg.style.height = '';
      movable.style.width = '';
    }
    setTimeout(() => setOverlay(calcOverlay(selectedImg)), 20);
    notify();
  }, [selectedImg, isFullWidth, getMovable, calcOverlay, notify]);

  const startDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImg) return;

    const movable  = getMovable(selectedImg);
    const origRect = movable.getBoundingClientRect();
    const startX   = e.clientX;
    const startY   = e.clientY;
    let hasMoved   = false;
    let dropTarget = null;
    let before     = true;

    // Ghost outline follows cursor
    const ghost = document.createElement('div');
    ghost.style.cssText = `position:fixed;z-index:9999;pointer-events:none;opacity:0.5;` +
      `width:${origRect.width}px;height:${origRect.height}px;` +
      `left:${origRect.left}px;top:${origRect.top}px;` +
      `border:2px dashed #6B8E23;border-radius:8px;` +
      `background:rgba(107,142,35,0.08);box-sizing:border-box;`;
    document.body.appendChild(ghost);
    movable.style.opacity = '0.25';

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) hasMoved = true;
      if (!hasMoved) return;

      ghost.style.left = (origRect.left + dx) + 'px';
      ghost.style.top  = (origRect.top  + dy) + 'px';

      // Find the editor child block under cursor (hide ghost first so it doesn't interfere)
      ghost.style.display = 'none';
      const hit = document.elementFromPoint(ev.clientX, ev.clientY);
      ghost.style.display = '';

      if (!hit || !editorRef.current?.contains(hit)) { setDropLine(null); dropTarget = null; return; }

      let block = hit;
      while (block && block.parentElement !== editorRef.current) block = block.parentElement;
      if (!block || block === movable || block === editorRef.current) { setDropLine(null); dropTarget = null; return; }

      const br  = block.getBoundingClientRect();
      before     = ev.clientY < br.top + br.height / 2;
      dropTarget = block;

      const cr = containerRef.current.getBoundingClientRect();
      setDropLine({ top: (before ? br.top : br.bottom) - cr.top });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      ghost.remove();
      movable.style.opacity = '';
      setDropLine(null);

      if (hasMoved && dropTarget && dropTarget !== movable) {
        if (before) {
          editorRef.current.insertBefore(movable, dropTarget);
        } else {
          dropTarget.after(movable);
        }
        notify();
        setTimeout(() => setOverlay(calcOverlay(selectedImg)), 50);
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [selectedImg, getMovable, calcOverlay, notify]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <EditorContainer ref={containerRef}>

      {/* ── Toolbar ── */}
      <Toolbar>

        {/* Block format */}
        <HeadingSelect value={headingValue} onMouseDown={saveSel} onChange={e => { applyBlock(e.target.value); setHeadingValue(e.target.value); }}>
          <option value="p">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="blockquote">Quote</option>
          <option value="pre">Code</option>
        </HeadingSelect>

        <ToolbarDivider />

        {/* Font size */}
        <FontSizeControl>
          <FontSizeBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => applyFontSize(fontSize - 1)}>
            <Minus size={11} />
          </FontSizeBtn>
          <FontSizeInput
            type="number"
            value={fontSize}
            min={8} max={96}
            onMouseDown={saveSel}
            onChange={e => applyFontSize(parseInt(e.target.value, 10) || 16)}
          />
          <FontSizeBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => applyFontSize(fontSize + 1)}>
            <Plus size={11} />
          </FontSizeBtn>
        </FontSizeControl>

        <ToolbarDivider />

        {/* Text format */}
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('bold')}          title="Bold (Ctrl+B)">      <Bold size={15} />        </TBtn>
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('italic')}        title="Italic (Ctrl+I)">    <Italic size={15} />      </TBtn>
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('underline')}     title="Underline (Ctrl+U)"> <Underline size={15} />   </TBtn>
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('strikeThrough')} title="Strikethrough">      <Strikethrough size={15} /></TBtn>

        <ToolbarDivider />

        {/* Text color */}
        <ColorPickerWrapper ref={textCPRef}>
          <TBtn
            type="button"
            onMouseDown={e => { e.preventDefault(); saveSel(); }}
            onClick={() => { setShowTextCP(v => !v); setShowHlCP(false); }}
            title="Text Color"
            $active={showTextCP}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Palette size={13} />
              <div style={{ width: 14, height: 3, background: textColor, borderRadius: 1 }} />
            </div>
          </TBtn>
          {showTextCP && (
            <ColorDropdown>
              <RemoveColorRow type="button" onClick={() => { exec('removeFormat'); setTextColor('#000000'); setShowTextCP(false); }}>
                <X size={12} /> Remove color
              </RemoveColorRow>
              <ColorGrid>
                {COLOR_PALETTE.map((c, i) => (
                  <Swatch key={i} type="button" $color={c} $sel={textColor === c} onClick={() => applyTextColor(c)} title={c} />
                ))}
              </ColorGrid>
            </ColorDropdown>
          )}
        </ColorPickerWrapper>

        {/* Highlight */}
        <ColorPickerWrapper ref={hlCPRef}>
          <TBtn
            type="button"
            onMouseDown={e => { e.preventDefault(); saveSel(); }}
            onClick={() => { setShowHlCP(v => !v); setShowTextCP(false); }}
            title="Highlight Color"
            $active={showHlCP}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Highlighter size={13} />
              <div style={{ width: 14, height: 3, background: hlColor === 'transparent' ? '#fff' : hlColor, borderRadius: 1 }} />
            </div>
          </TBtn>
          {showHlCP && (
            <ColorDropdown>
              <RemoveColorRow type="button" onClick={() => { exec('hiliteColor', 'transparent'); setHlColor('#ffff00'); setShowHlCP(false); }}>
                <X size={12} /> Remove highlight
              </RemoveColorRow>
              <ColorGrid>
                {COLOR_PALETTE.map((c, i) => (
                  <Swatch key={i} type="button" $color={c} $sel={hlColor === c} onClick={() => applyHighlight(c)} title={c} />
                ))}
              </ColorGrid>
            </ColorDropdown>
          )}
        </ColorPickerWrapper>

        <ToolbarDivider />

        {/* Alignment */}
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('justifyLeft')}   title="Align Left">   <AlignLeft size={15} />   </TBtn>
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('justifyCenter')} title="Align Center"> <AlignCenter size={15} /> </TBtn>
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('justifyRight')}  title="Align Right">  <AlignRight size={15} />  </TBtn>

        <ToolbarDivider />

        {/* Lists */}
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('insertUnorderedList')} title="Bullet List">   <List size={15} />        </TBtn>
        <TBtn type="button" onMouseDown={e => { e.preventDefault(); saveSel(); }} onClick={() => exec('insertOrderedList')}   title="Numbered List"> <ListOrdered size={15} /> </TBtn>

        <ToolbarDivider />

        {/* Link */}
        <TBtn
          type="button"
          onMouseDown={e => { e.preventDefault(); saveSel(); }}
          onClick={() => { setShowLinkBar(v => !v); setShowMediaBar(false); }}
          title="Insert Link"
          $active={showLinkBar}
        >
          <LinkIcon size={15} />
        </TBtn>

        {/* Image / video */}
        <TBtn
          type="button"
          onMouseDown={e => { e.preventDefault(); saveSel(); }}
          onClick={() => { setShowMediaBar(v => !v); setShowLinkBar(false); }}
          title="Insert Image or Video URL"
          $active={showMediaBar}
        >
          <ImageIcon size={15} />
        </TBtn>

        {/* Divider */}
        <TBtn
          type="button"
          onMouseDown={e => { e.preventDefault(); saveSel(); }}
          onClick={() => exec('insertHorizontalRule')}
          title="Insert Divider"
          style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-1px' }}
        >
          —
        </TBtn>

      </Toolbar>

      {/* ── Link bar ── */}
      {showLinkBar && (
        <InsertBar>
          <input
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); doInsertLink(); }
              if (e.key === 'Escape') { setShowLinkBar(false); setLinkUrl(''); }
            }}
            placeholder="Enter URL — e.g. https://example.com"
            autoFocus
          />
          <IconBtn type="button" $primary onClick={doInsertLink}> <Check size={15} /> </IconBtn>
          <IconBtn type="button" onClick={() => { setShowLinkBar(false); setLinkUrl(''); }}> <X size={15} /> </IconBtn>
        </InsertBar>
      )}

      {/* ── Media bar ── */}
      {showMediaBar && (
        <InsertBar>
          <InsertRow>
            <input
              value={mediaUrl}
              onChange={e => setMediaUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); doInsertMedia(); }
                if (e.key === 'Escape') { setShowMediaBar(false); setMediaUrl(''); setHideControls(false); setLoopVideo(false); setAutoplayVideo(false); }
              }}
              placeholder="Paste image or video URL (YouTube, Vimeo, or direct .jpg / .mp4 link)…"
              autoFocus
            />
            <IconBtn type="button" $primary onClick={doInsertMedia}> <Check size={15} /> </IconBtn>
            <IconBtn type="button" onClick={() => { setShowMediaBar(false); setMediaUrl(''); setHideControls(false); setLoopVideo(false); setAutoplayVideo(false); }}> <X size={15} /> </IconBtn>
          </InsertRow>
          {/\.(mp4|webm|ogg)(\?|$)/i.test(mediaUrl) && (
            <VideoOptions>
              <ToggleLabel>
                <input type="checkbox" checked={hideControls} onChange={e => setHideControls(e.target.checked)} />
                <ToggleTrack $on={hideControls}><ToggleThumb $on={hideControls} /></ToggleTrack>
                Hide player UI
              </ToggleLabel>
              <ToggleLabel>
                <input type="checkbox" checked={loopVideo} onChange={e => setLoopVideo(e.target.checked)} />
                <ToggleTrack $on={loopVideo}><ToggleThumb $on={loopVideo} /></ToggleTrack>
                Loop
              </ToggleLabel>
              <ToggleLabel>
                <input type="checkbox" checked={autoplayVideo} onChange={e => setAutoplayVideo(e.target.checked)} />
                <ToggleTrack $on={autoplayVideo}><ToggleThumb $on={autoplayVideo} /></ToggleTrack>
                Autoplay
              </ToggleLabel>
            </VideoOptions>
          )}
        </InsertBar>
      )}

      {/* ── Editor ── */}
      <EditorArea
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={notify}
        onMouseDown={saveSel}
        onKeyUp={() => { saveSel(); detectState(); }}
        onMouseUp={() => { saveSel(); detectState(); }}
        onBlur={() => { saveSel(); notify(); }}
        onPaste={handlePaste}
        onClick={handleEditorClick}
      />

      {/* ── Media resize + drag overlay ── */}
      {overlay && (
        <ResizeBox style={{ left: overlay.left, top: overlay.top, width: overlay.width, height: overlay.height }}>
          <DragHandle onMouseDown={startDrag} title="Drag to reposition">
            <Move size={12} />
          </DragHandle>
          {!isFullWidth && ['nw','ne','sw','se','n','s','e','w'].map(h => (
            <ResizeHandle key={h} $pos={h} onMouseDown={e => startResize(e, h)} />
          ))}
          <MediaControls>
            <MediaControlBtn
              type="button"
              $active={isFullWidth}
              onMouseDown={e => e.preventDefault()}
              onClick={toggleFullWidth}
            >
              Full Width
            </MediaControlBtn>
          </MediaControls>
        </ResizeBox>
      )}

      {/* ── Drag drop-line indicator ── */}
      {dropLine && <DropLine style={{ top: dropLine.top }} />}

    </EditorContainer>
  );
};
